import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import renderSelectField from '../../../../shared/components/form/Select';
import renderCheckBoxField from '../../../../shared/components/form/CheckBox';
import Tooltip from '../../../../shared/components/widgets/Tooltip';
import { renderTextArea } from '../../shared/formComponents';
import {
  ITEM_TYPE_OPTIONS,
  ITEM_SELECTION_OPTIONS,
  DESTINATION_OPTIONS,
} from '../../shared/constants';

const ItemBudgetFields = ({
  setupFlow,
  agencyInternal,
  masterItemOnList,
  masterItemOffList,
  currentEditRow,
  retailerConfig,
  selectedSeller,
  itemCsvInput,
  isExternalBudgetGroup,
}) => {
  // Target only supports a single "Item" item type (no SKU/ASIN distinction)
  const filteredItemTypeOptions = retailerConfig?.key === 'target'
    ? ITEM_TYPE_OPTIONS.filter(option => option.value === 'ITEM')
    : ITEM_TYPE_OPTIONS;

  return (
    <>
      {setupFlow?.value === 'advanced' && agencyInternal?.value && (
      <div className="form__form-group">
        <span className="form__form-group-label">Item Type</span>
        <div className="form__form-group-field">
          <Field
            name="itemType"
            type="text"
            component={renderSelectField}
            options={filteredItemTypeOptions}
          />
        </div>
      </div>
      )}

      {setupFlow?.value === 'advanced' && agencyInternal?.value && (
      <div className="form__form-group">
        <span className="form__form-group-label">Item Selection</span>
        <div className="form__form-group-field">
          <Field
            name="itemSelection"
            type="text"
            component={renderSelectField}
            options={ITEM_SELECTION_OPTIONS}
          />
        </div>
      </div>
      )}

      {setupFlow?.value === 'advanced' && agencyInternal?.value && (
      <div className="form__form-group">
        <span className="form__form-group-label">Destination <Tooltip id="destination" msg="Describes where we are going to take ad traffic (ON or OFF)" placement="top" /></span>
        <div className="form__form-group-field">
          <Field
            name="destination"
            type="text"
            component={renderSelectField}
            options={DESTINATION_OPTIONS}
          />
        </div>
      </div>
      )}

      {/* Hide ON/OFF text areas for External budget groups */}
      {!isExternalBudgetGroup && (
      <>
        <div className="form__form-group">
          <span className="form__form-group-label">
            ON&nbsp;
            {(() => {
              if (retailerConfig?.key === 'amazon') {
                return selectedSeller.type === 1 ? 'SKUs' : 'ASINs';
              }
              return 'Items';
            })()}
            &nbsp;(<a href="/self-service/budget#Items" target="_blank" rel="noopener noreferrer">View Catalog HERE</a>)&nbsp;
            <Tooltip id="masterItemOnList" msg="Items in this list will have their Ad Status turned ON" placement="top" />
          </span>
          <div className="form__form-group-field">
            <Field
              name="masterItemOnList"
              type="text"
              component={renderTextArea}
              format={itemCsvInput}
            />
          </div>
          {masterItemOnList && (() => {
            const itemList = (masterItemOnList || '').split(',').map(item => item.trim()).filter(item => item);
            const itemCount = itemList.length;
            const upperCaseItems = itemList.map(item => item.toUpperCase());
            const hasDuplicates = new Set(upperCaseItems).size !== itemList.length;

            if (hasDuplicates && itemCount > 0) {
              return (
                <div style={{ marginTop: '10px', padding: '10px', borderRadius: '4px', border: '1px solid rgba(255, 165, 0, 0.4)', backgroundColor: 'rgba(255, 165, 0, 0.1)' }}>
                  <div style={{ fontWeight: '500', color: '#ff8c00' }}>
                    ⚠ Duplicate items found in list
                  </div>
                </div>
              );
            }
            return null;
          })()}
        </div>

        <div className="form__form-group">
          <span className="form__form-group-label">
            OFF&nbsp;
            {(() => {
              if (retailerConfig?.key === 'amazon') {
                return selectedSeller.type === 1 ? 'SKUs' : 'ASINs';
              }
              return 'Items';
            })()}
            {' '}<Tooltip id="masterItemOffList" msg="Items in this list will have their Ad Status turned OFF" placement="top" />
          </span>
          <div className="form__form-group-field">
            <Field
              name="masterItemOffList"
              type="text"
              component={renderTextArea}
              format={itemCsvInput}
            />
          </div>
          {masterItemOnList && masterItemOffList && (() => {
            const onItems = (masterItemOnList || '').split(',').map(item => item.trim().toUpperCase()).filter(item => item);
            const offItems = (masterItemOffList || '').split(',').map(item => item.trim().toUpperCase()).filter(item => item);
            const duplicates = onItems.filter(item => offItems.includes(item));
            return duplicates.length > 0 ? (
              <div style={{ marginTop: '10px', padding: '10px', borderRadius: '4px', border: '1px solid rgba(255, 0, 0, 0.4)', backgroundColor: 'rgba(255, 0, 0, 0.1)' }}>
                <div style={{ marginBottom: '5px', fontWeight: '500', color: '#ff4861' }}>❌ Duplicate Items Found:</div>
                <div style={{ fontSize: '0.9em', color: '#721c24' }}>
                  {duplicates.join(', ')}
                </div>
              </div>
            ) : null;
          })()}
        </div>
      </>
      )}

      {setupFlow?.value === 'advanced' && !(currentEditRow && currentEditRow.budget_group_daily_id_csv) && !isExternalBudgetGroup && (
      <div className="form__form-group">
        <span className="form__form-group-label">Create A Budget Group for each Item listed (Advanced)</span>
        <div className="form__form-group-field">
          <Field
            name="splitItemsCheckbox"
            component={renderCheckBoxField}
            label="Split Items Into Individual Budget Groups"
          />
        </div>
      </div>
      )}
    </>
  );
};

ItemBudgetFields.propTypes = {
  setupFlow: PropTypes.shape(),
  agencyInternal: PropTypes.shape(),
  masterItemOnList: PropTypes.string,
  masterItemOffList: PropTypes.string,
  currentEditRow: PropTypes.shape(),
  retailerConfig: PropTypes.shape(),
  selectedSeller: PropTypes.shape().isRequired,
  itemCsvInput: PropTypes.func.isRequired,
  isExternalBudgetGroup: PropTypes.bool,
};

ItemBudgetFields.defaultProps = {
  setupFlow: null,
  agencyInternal: null,
  masterItemOnList: '',
  masterItemOffList: '',
  currentEditRow: null,
  retailerConfig: null,
  isExternalBudgetGroup: false,
};

export default ItemBudgetFields;
